
/* ================================
   SHOPIFY GRAPHQL CONFIG
================================ */
const STOREFRONT_TOKEN = '6cd5cdedcc8038237a20d4ac83ef8f9d';
const GRAPHQL_ENDPOINT = 'https://nurocore-dk.myshopify.com/api/2024-01/graphql.json';




/* ================================
   GRAPHQL HELPER
================================ */
async function shopifyGraphQL(query, variables = {}) {
  const response = await fetch(GRAPHQL_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
    },
    body: JSON.stringify({ query, variables })
  });

  const result = await response.json();

  if (result.errors) {
    console.error('GraphQL Errors:', result.errors);
    throw new Error('GraphQL request failed');
  }

  return result.data;
}

/* ================================
   GRAPHQL QUERY - Search by Variant SKU
================================ */
const PRODUCT_BY_SKU_QUERY = `
query ProductBySku($query: String!) {
  products(first: 50, query: $query) {
    edges {
      node {
        id
        title
        handle
        featuredImage {
          url
        }
        variants(first: 100) {
          edges {
            node {
              id
              sku
              title
            }
          }
        }
        metafields(identifiers: [
          { namespace: "custom", key: "batch_number" }
          { namespace: "custom", key: "production_date" }
          { namespace: "custom", key: "purity_test_certificate" }
          { namespace: "custom", key: "strength_test_certificate" }
          { namespace: "custom", key: "heavy_metal_test_certificate" }
          { namespace: "custom", key: "microbiological_test_certificate" }
        ]) {
          key
          value
          reference {
            ... on GenericFile {
              url
            }
            ... on MediaImage {
              image {
                url
              }
            }
          }
        }
      }
    }
  }
}
`;

/* ================================
   FETCH PRODUCT BY SKU
   Searches products by variant SKU (exact match)
================================ */
async function fetchProductBySku(sku) {
  const searchSku = sku.trim().toUpperCase();
  console.log('🔍 Searching for SKU:', searchSku);
  
  // Search for products - try SKU query first
  let queryString = `sku:${searchSku}`;
  console.log('GraphQL Query:', queryString);
  
  let data = await shopifyGraphQL(PRODUCT_BY_SKU_QUERY, {
    query: queryString
  });

  console.log('GraphQL Response - Products found:', data.products.edges.length);

  // Find the product that has a variant with exact SKU match
  let matchedProduct = null;
  let matchedVariant = null;
  
  // Check all products and their variants for exact SKU match
  for (const edge of data.products.edges) {
    const product = edge.node;
    
    // Check all variants for exact SKU match
    if (product.variants && product.variants.edges) {
      for (const variantEdge of product.variants.edges) {
        const variant = variantEdge.node;
        const variantSku = variant.sku ? variant.sku.trim().toUpperCase() : '';
        
        // Exact SKU match (case-insensitive)
        if (variantSku === searchSku) {
          console.log('✅ Exact SKU match found!');
          console.log('  Product:', product.title);
          console.log('  Variant SKU:', variant.sku);
          console.log('  Variant Title:', variant.title);
          matchedProduct = product;
          matchedVariant = variant;
          break;
        }
      }
    }
    
    if (matchedProduct) break;
  }

  // If no match found with SKU query, try searching all products
  if (!matchedProduct) {
    console.log('⚠️ No match with SKU query, trying broader search...');
    queryString = '*';
    data = await shopifyGraphQL(PRODUCT_BY_SKU_QUERY, {
      query: queryString
    });
    
    console.log('Broad search - Products found:', data.products.edges.length);
    
    // Search through all products
    for (const edge of data.products.edges) {
      const product = edge.node;
      
      if (product.variants && product.variants.edges) {
        for (const variantEdge of product.variants.edges) {
          const variant = variantEdge.node;
          const variantSku = variant.sku ? variant.sku.trim().toUpperCase() : '';
          
          if (variantSku === searchSku) {
            console.log('✅ Exact SKU match found in broad search!');
            console.log('  Product:', product.title);
            console.log('  Variant SKU:', variant.sku);
            matchedProduct = product;
            matchedVariant = variant;
            break;
          }
        }
      }
      
      if (matchedProduct) break;
    }
  }

  if (!matchedProduct) {
    console.log('❌ No product found with exact SKU match:', searchSku);
    return null;
  }

  console.log('✅ Using product:', matchedProduct.title, 'ID:', matchedProduct.id);
  if (matchedVariant) {
    console.log('✅ Matched variant:', matchedVariant.title, 'SKU:', matchedVariant.sku);
  }

  // Extract metafields with validation
  const metafields = {};

  if (matchedProduct.metafields && Array.isArray(matchedProduct.metafields)) {
    matchedProduct.metafields.forEach(mf => {
      if (mf && mf.key) {
        const value = mf.reference?.url ||
          mf.reference?.image?.url ||
          mf.value ||
          '';
        
        // Only add metafield if it has a value
        if (value && value.trim() !== '') {
          metafields[mf.key] = value;
        }
      }
    });
  }

  console.log('📦 Product metafields (with values only):', metafields);

  return {
    title: matchedProduct.title || '',
    image: matchedProduct.featuredImage?.url || '',
    metafields
  };
}

/* ================================
   SKU PRODUCT SEARCH CLASS
================================ */
class SkuProductSearch {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.form = document.getElementById(`SkuSearchForm-${sectionId}`);
    this.input = document.getElementById(`SkuInput-${sectionId}`);
    this.results = document.getElementById(`SkuSearchResults-${sectionId}`);
    this.error = document.getElementById(`SkuSearchError-${sectionId}`);
    this.button = document.getElementById(`SkuSearchButton-${sectionId}`);

    this.init();
  }

  init() {
    if (!this.form) return;

    this.button?.setAttribute('data-original-text', this.button.textContent);

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.searchProduct();
    });
  }

  async searchProduct() {
    const sku = this.input.value.trim().toUpperCase();
    if (!sku) return this.showError('Please enter SKU');

    console.log('🔎 Starting search for SKU:', sku);

    this.setLoading(true);
    this.hideError();
    this.hideResults();

    try {
      const product = await fetchProductBySku(sku);

      if (!product) {
        console.log('❌ Product not found for SKU:', sku);
        this.showError(`Product with SKU "${sku}" not found. Please check your SKU number.`);
        return;
      }

      console.log('✅ Displaying product:', product.title);

      // Only include metafields that have values
      const metafields = product.metafields || {};
      
      // Check if all 4 certificate metafields are null/empty
      const certificates = {
        purity: metafields.purity_test_certificate || '',
        strength: metafields.strength_test_certificate || '',
        heavyMetal: metafields.heavy_metal_test_certificate || '',
        microbiological: metafields.microbiological_test_certificate || ''
      };
      
      const certValues = [
        certificates.purity,
        certificates.strength,
        certificates.heavyMetal,
        certificates.microbiological
      ];
      
      const hasAnyCertificate = certValues.some(url => url && url.trim() !== '');
      
      // If all certificates are null, show error message and don't display product
      if (!hasAnyCertificate) {
        console.log('❌ No certificates found for product:', product.title);
        this.showError('No certificate file for this product');
        this.hideResults();
        return;
      }
      
      this.displayProduct({
        title: product.title,
        image: product.image,
        batchNumber: metafields.batch_number || null,
        productionDate: metafields.production_date || null,
        certificates: certificates
      });

    } catch (e) {
      console.error('❌ Error searching product:', e);
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  displayProduct(product) {
    // Set text values for meta fields (Batch Number, Production Date)
    const setText = (id, value) => {
      const el = document.getElementById(`${id}-${this.sectionId}`);
      if (el) {
        if (value && value !== null && value !== '') {
          el.textContent = value;
          el.closest('.sku-product-search__meta-item')?.style.setProperty('display', 'block');
        } else {
          // Hide the entire meta item if no value
          el.closest('.sku-product-search__meta-item')?.style.setProperty('display', 'none');
        }
      }
    };

    // Set image
    const img = document.getElementById(`SkuProductImage-${this.sectionId}`);
    if (img && product.image) {
      img.src = product.image;
      img.alt = product.title || '';
    }

    // Set product title (always show - it's inside a meta-item now)
    const titleEl = document.getElementById(`SkuProductTitle-${this.sectionId}`);
    if (titleEl) {
      titleEl.textContent = product.title || '';
      // Show the product name meta-item
      titleEl.closest('.sku-product-search__meta-item')?.style.setProperty('display', 'block');
    }

    // Set metafield values (only if they exist)
    setText('SkuBatchNumber', product.batchNumber);
    setText('SkuProductionDate', product.productionDate);

    // Set certificate links
    const certificates = product.certificates || {};
    const certValues = [
      certificates.purity,
      certificates.strength,
      certificates.heavyMetal,
      certificates.microbiological
    ];
    
    // Check if any certificate has value (we already checked before, but double-check)
    const hasCertificates = certValues.some(url => url && url.trim() !== '');
    
    // Show/hide certificates section
    const certSection = document.getElementById(`SkuCertificatesSection-${this.sectionId}`);
    const noCertMessage = document.getElementById(`SkuNoCertificates-${this.sectionId}`);
    
    if (certSection) {
      if (hasCertificates) {
        // Show certificates section with title and grid
        certSection.style.display = 'block';
        if (noCertMessage) noCertMessage.style.display = 'none';
      } else {
        // Hide certificates section
        certSection.style.display = 'none';
      }
    }

    // Set individual certificate links
    const setLink = (id, url) => {
      const el = document.getElementById(`${id}-${this.sectionId}`);
      if (!el) return;
      if (url && url.trim() !== '') {
        el.href = url;
        el.style.display = 'inline-flex';
      } else {
        el.style.display = 'none';
      }
    };

    setLink('CertificatePurity', certificates.purity);
    setLink('CertificateStrength', certificates.strength);
    setLink('CertificateHeavyMetal', certificates.heavyMetal);
    setLink('CertificateMicrobiological', certificates.microbiological);

    this.showResults();
  }

  showResults() {
    this.results.style.display = 'block';
  }

  hideResults() {
    this.results.style.display = 'none';
  }

  showError(msg) {
    if (this.error) {
      this.error.querySelector('.sku-product-search__error-message').textContent = msg;
      this.error.style.display = 'block';
    }
  }

  hideError() {
    this.error.style.display = 'none';
  }

  setLoading(state) {
    if (!this.button) return;
    this.button.disabled = state;
    this.button.textContent = state ? 'Searching…' : this.button.dataset.originalText;
  }
}

/* ================================
   INIT
================================ */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.sku-product-search').forEach(section => {
    const form = section.querySelector('[id^="SkuSearchForm-"]');
    if (!form) return;
    const id = form.id.replace('SkuSearchForm-', '');
    new SkuProductSearch(id);
  });
});

